/**
 * ONE TAP — the adaptive generative score.
 *
 * Cel-shaded, kinetic, breakbeat/electro. Everything is synthesised and everything is
 * sequenced at runtime: procedural drums, a filtered bassline, a rotating arpeggio, a pad,
 * and stabs that only arrive when the round gets desperate.
 *
 * Two rules govern the whole thing:
 *
 *  1. **It must never audibly loop.** Rhythms are regenerated every bar from a per-bar
 *     deterministic RNG, so the drum and bass patterns have no period at all. Only the
 *     8-bar harmonic progression repeats, and that is what makes it sound composed rather
 *     than random.
 *  2. **It must never fight the SFX.** Fixed key of A minor, the bass sits under 220 Hz, and
 *     the whole bus ducks on the kick (a classic electro pump that doubles as headroom) and
 *     again whenever the game fires a gun. The score gets out of the way of the gunshot,
 *     always.
 *
 * Layers mix in and out on round intensity: time remaining, how many players are alive, and
 * whether the local player is holding a chambered gun.
 */

import { clamp, clamp01, lerpN, makeRng } from '@shared/math';
import {
  MIN_GAIN,
  NoiseFactory,
  Rng,
  ShaperCurve,
  biquad,
  gain,
  makeAsymCurve,
  makeDistortionCurve,
  noteHz,
  osc,
  percEnv,
  pitchEnv,
  play,
  shaper,
  startAtRandomPhase,
  stopSafe,
  sweep,
} from './synth';

export type MusicState = 'silent' | 'menu' | 'lobby' | 'warmup' | 'live' | 'suddendeath' | 'roundend' | 'matchend';

const BPM = 168;
const STEPS_PER_BAR = 16;
const STEP_SEC = 60 / BPM / 4;
const LOOKAHEAD_SEC = 0.18;
const TIMER_MS = 24;
/** Never schedule more than this in one pump — a throttled background tab must not stampede. */
const MAX_STEPS_PER_PUMP = 48;

/** A minor. Roots are semitones above A. */
const PROGRESSION: ReadonlyArray<{ root: number; third: number; seventh: number }> = [
  { root: 0, third: 3, seventh: 10 }, // Am7
  { root: 0, third: 3, seventh: 10 }, // Am7
  { root: 8, third: 4, seventh: 11 }, // Fmaj7
  { root: 10, third: 4, seventh: 10 }, // G
  { root: 3, third: 4, seventh: 11 }, // Cmaj7
  { root: 0, third: 3, seventh: 10 }, // Am7
  { root: 5, third: 3, seventh: 10 }, // Dm7
  { root: 7, third: 4, seventh: 10 }, // E (dominant — pulls back to Am)
];

/** Four-step kick cells. Broken, syncopated, never a four-on-the-floor. */
const KICK_CELLS: ReadonlyArray<ReadonlyArray<number>> = [
  [0],
  [0, 3],
  [0, 2],
  [2],
  [1],
  [],
  [0, 3],
  [3],
];

/** Four-step bass rhythm cells. */
const BASS_CELLS: ReadonlyArray<ReadonlyArray<number>> = [
  [0, 2, 3],
  [0, 3],
  [0, 1, 3],
  [0, 2],
  [1, 3],
  [0],
  [0, 2, 3],
  [2, 3],
];

interface BarPlan {
  chord: { root: number; third: number; seventh: number };
  kick: number[];
  snare: number[];
  hat: number[];
  openHat: boolean[];
  bass: Array<number | null>;
  arp: Array<number | null>;
  lead: Array<number | null>;
  perc: number[];
}

interface Layer {
  gain: GainNode;
  target: number;
}

export class Music {
  private readonly master: GainNode;
  private readonly duckGain: GainNode;
  private readonly pump: GainNode;
  private readonly tone: BiquadFilterNode;
  private readonly subCut: BiquadFilterNode;

  private readonly layers: Record<'drums' | 'bass' | 'arp' | 'pad' | 'lead' | 'perc', Layer>;

  private readonly delay: DelayNode;
  private readonly delayFb: GainNode;
  private readonly delaySend: GainNode;
  private readonly verbSend: GainNode | null;

  private readonly curveDrive: ShaperCurve;
  private readonly curveSat: ShaperCurve;

  private readonly seed: number;
  private readonly noise: NoiseFactory;
  private readonly rng: Rng;

  private timer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  private nextStepTime = 0;
  private plan: BarPlan | null = null;

  private state: MusicState = 'silent';
  private intensity = 0;
  private volume = 1;
  private running = false;
  private disposed = false;

  constructor(
    private readonly ctx: AudioContext,
    destination: AudioNode,
    reverbBuffer: AudioBuffer | null,
    noise: NoiseFactory,
    seed = 0x53c04e,
  ) {
    this.seed = seed >>> 0;
    this.noise = noise;
    this.rng = makeRng(this.seed);

    this.curveDrive = makeDistortionCurve(1.1);
    this.curveSat = makeAsymCurve(1.5);

    this.master = gain(ctx, 0);
    this.duckGain = gain(ctx, 1);
    this.pump = gain(ctx, 1);
    this.tone = biquad(ctx, 'lowpass', 3200, 0.7);

    // Highpass the whole score at 46 Hz.
    //
    // The bassline's sub oscillator runs an octave below a note clamped to 41 Hz, so the
    // score was putting energy down to ~20 Hz — inaudible on any speaker a player owns, and
    // occupying exactly the band the gunshot's sub needs. Every dB of headroom the music
    // spends below 46 Hz is a dB the shot does not get, and the shot is the whole game.
    this.subCut = biquad(ctx, 'highpass', 46, 0.6);

    this.pump.connect(this.tone);
    this.tone.connect(this.subCut);
    this.subCut.connect(this.duckGain);
    this.duckGain.connect(this.master);
    this.master.connect(destination);

    const mk = (v: number): Layer => {
      const g = gain(ctx, v);
      g.connect(this.pump);
      return { gain: g, target: v };
    };
    this.layers = {
      drums: mk(0),
      bass: mk(0),
      arp: mk(0),
      pad: mk(0),
      lead: mk(0),
      perc: mk(0),
    };

    // Dotted-eighth delay for the arpeggio — the classic electro shimmer.
    this.delay = ctx.createDelay(1.0);
    this.delay.delayTime.value = STEP_SEC * 3;
    this.delayFb = gain(ctx, 0.34);
    this.delaySend = gain(ctx, 1);
    const delayTone = biquad(ctx, 'bandpass', 1800, 0.6);
    this.delaySend.connect(this.delay);
    this.delay.connect(delayTone);
    delayTone.connect(this.delayFb);
    this.delayFb.connect(this.delay);
    delayTone.connect(this.layers.arp.gain);

    // Music gets its own plate convolver from the shared impulse so its wet return stays
    // on the music bus and is not affected by the SFX fader.
    if (reverbBuffer) {
      const conv = ctx.createConvolver();
      conv.normalize = false;
      conv.buffer = reverbBuffer;
      const ret = gain(ctx, 0.5);
      this.verbSend = gain(ctx, 1);
      const pre = biquad(ctx, 'highpass', 260, 0.7);
      this.verbSend.connect(pre);
      pre.connect(conv);
      conv.connect(ret);
      ret.connect(this.pump);
    } else {
      this.verbSend = null;
    }
  }

  // -------------------------------------------------------------------------
  // Transport
  // -------------------------------------------------------------------------

  start(): void {
    if (this.disposed || this.running) return;
    this.running = true;
    this.nextStepTime = this.ctx.currentTime + 0.08;
    this.timer = setInterval(() => this.pumpScheduler(), TIMER_MS);
    this.applyMix(0.6);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(Math.max(this.master.gain.value, MIN_GAIN), now);
    this.master.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.5);
  }

  setVolume(v: number): void {
    this.volume = clamp(v, 0, 2);
    if (!this.disposed) this.applyMix(0.2);
  }

  /** 0..1 — how hot the round is. Drives which stems are audible and how open the bus is. */
  setIntensity(v: number): void {
    const next = clamp01(v);
    if (Math.abs(next - this.intensity) < 0.01) return;
    this.intensity = next;
    this.applyMix(0.7);
  }

  setState(s: MusicState): void {
    if (s === this.state) return;
    this.state = s;
    this.applyMix(s === 'live' || s === 'suddendeath' ? 0.35 : 0.8);
  }

  /**
   * Momentarily pull the score down. The engine calls this on gunshots and kills so the
   * loudest events in the game always have the mix to themselves.
   */
  duck(amount: number, seconds = 0.45): void {
    if (this.disposed) return;
    const now = this.ctx.currentTime;
    const floor = clamp(1 - amount, 0.03, 1);
    const p = this.duckGain.gain;
    p.cancelScheduledValues(now);
    p.setValueAtTime(Math.max(Math.min(p.value, 1), MIN_GAIN), now);
    // 5 ms, not 20. The hole has to already be there when the transient arrives — a duck
    // that takes 20 ms to open is a duck that the gunshot's own attack plays straight over,
    // which is the same as no duck at all for the one moment it exists to serve.
    p.linearRampToValueAtTime(floor, now + 0.005);
    p.setValueAtTime(floor, now + Math.max(seconds * 0.3, 0.05));
    p.linearRampToValueAtTime(1, now + Math.max(seconds, 0.12));
  }

  dispose(): void {
    if (this.disposed) return;
    this.stop();
    this.disposed = true;
    try {
      this.master.disconnect();
      this.pump.disconnect();
      this.tone.disconnect();
      this.subCut.disconnect();
      this.duckGain.disconnect();
      this.delay.disconnect();
      this.delayFb.disconnect();
      this.delaySend.disconnect();
      this.verbSend?.disconnect();
      for (const l of Object.values(this.layers)) l.gain.disconnect();
    } catch {
      /* graph already torn down */
    }
  }

  // -------------------------------------------------------------------------
  // Mixing
  // -------------------------------------------------------------------------

  private applyMix(rampSec: number): void {
    const i = this.intensity;
    const L = this.layers;

    let drums = 0;
    let bass = 0;
    let arp = 0;
    let pad = 0;
    let lead = 0;
    let perc = 0;
    let toneHz = 3200;
    let bus = 0.55;

    switch (this.state) {
      case 'silent':
        bus = 0;
        break;
      case 'menu':
      case 'lobby':
        pad = 0.9;
        arp = 0.5;
        bass = 0.35;
        drums = 0.22;
        toneHz = 2200;
        bus = 0.5;
        break;
      case 'warmup':
        pad = 0.8;
        arp = 0.4;
        bass = 0.55;
        drums = 0.5;
        toneHz = 2600;
        bus = 0.5;
        break;
      case 'live':
        drums = 1;
        bass = 1;
        pad = lerpN(0.75, 0.3, i);
        arp = clamp01((i - 0.18) / 0.35);
        perc = clamp01((i - 0.42) / 0.35);
        lead = clamp01((i - 0.72) / 0.28) * 0.85;
        toneHz = lerpN(2400, 7200, i);
        bus = lerpN(0.5, 0.72, i);
        break;
      case 'suddendeath':
        drums = 1;
        bass = 1;
        pad = 0.25;
        arp = 1;
        perc = 1;
        lead = 1;
        toneHz = 8500;
        bus = 0.78;
        break;
      case 'roundend':
        pad = 0.9;
        arp = 0.25;
        bass = 0.2;
        drums = 0.1;
        toneHz = 1600;
        bus = 0.45;
        break;
      case 'matchend':
        pad = 1;
        arp = 0.6;
        bass = 0.4;
        drums = 0.25;
        toneHz = 3000;
        bus = 0.55;
        break;
    }

    const now = this.ctx.currentTime;
    const tau = Math.max(rampSec, 0.05) / 3;
    const set = (layer: Layer, v: number, scale: number) => {
      layer.target = v;
      layer.gain.gain.setTargetAtTime(v * scale, now, tau);
    };
    set(L.drums, drums, 0.85);
    set(L.bass, bass, 0.8);
    set(L.arp, arp, 0.42);
    set(L.pad, pad, 0.3);
    set(L.lead, lead, 0.3);
    set(L.perc, perc, 0.34);

    this.tone.frequency.setTargetAtTime(toneHz, now, tau);
    this.master.gain.setTargetAtTime(this.running ? bus * this.volume : 0, now, tau);
  }

  // -------------------------------------------------------------------------
  // Sequencer
  // -------------------------------------------------------------------------

  private pumpScheduler(): void {
    if (!this.running || this.disposed) return;
    const ctx = this.ctx;
    if (ctx.state !== 'running') {
      this.nextStepTime = ctx.currentTime + 0.05;
      return;
    }
    const now = ctx.currentTime;
    // A throttled tab can leave us far behind. Resync rather than firing a hundred notes.
    if (this.nextStepTime < now - 0.5) this.nextStepTime = now + 0.02;

    let guard = 0;
    while (this.nextStepTime < now + LOOKAHEAD_SEC && guard++ < MAX_STEPS_PER_PUMP) {
      this.scheduleStep(this.step, this.nextStepTime);
      this.step++;
      this.nextStepTime += STEP_SEC;
    }
  }

  /** Regenerate the bar's material. Fresh every bar, so nothing has an audible period. */
  private makePlan(bar: number): BarPlan {
    const rng = makeRng((this.seed ^ Math.imul(bar + 1, 2654435761)) >>> 0);
    const chord = PROGRESSION[bar % PROGRESSION.length]!;
    const phraseStep = bar % 4;
    const fill = phraseStep === 3;
    const dense = this.intensity > 0.5 || this.state === 'suddendeath';

    const kick: number[] = new Array(STEPS_PER_BAR).fill(0);
    const snare: number[] = new Array(STEPS_PER_BAR).fill(0);
    const hat: number[] = new Array(STEPS_PER_BAR).fill(0);
    const openHat: boolean[] = new Array(STEPS_PER_BAR).fill(false);
    const bass: Array<number | null> = new Array(STEPS_PER_BAR).fill(null);
    const arp: Array<number | null> = new Array(STEPS_PER_BAR).fill(null);
    const lead: Array<number | null> = new Array(STEPS_PER_BAR).fill(null);
    const perc: number[] = new Array(STEPS_PER_BAR).fill(0);

    // --- Kick: cell per beat, but beat one always lands so the bar is readable.
    for (let beat = 0; beat < 4; beat++) {
      const cell = KICK_CELLS[(rng() * KICK_CELLS.length) | 0]!;
      for (const off of cell) kick[beat * 4 + off] = 0.85 + rng() * 0.15;
    }
    kick[0] = 1;

    // --- Snare: immovable backbeat plus ghosts. The backbeat is what makes it a breakbeat.
    snare[4] = 1;
    snare[12] = 1;
    const ghostSlots = [2, 6, 7, 10, 11, 14, 15];
    for (const g of ghostSlots) {
      if (rng() < (dense ? 0.3 : 0.18)) snare[g] = 0.18 + rng() * 0.22;
    }
    if (fill) {
      for (let s = 13; s < 16; s++) snare[s] = Math.max(snare[s]!, 0.45 + rng() * 0.4);
      snare[14] = 0.7;
    }

    // --- Hats: straight 16ths with velocity shaping; offbeats accented.
    for (let s = 0; s < STEPS_PER_BAR; s++) {
      const onBeat = s % 4 === 0;
      const offBeat = s % 4 === 2;
      let v = onBeat ? 0.55 : offBeat ? 0.8 : 0.32;
      if (!dense && s % 2 === 1) v *= 0.35;
      v *= 0.75 + rng() * 0.4;
      hat[s] = v;
      if (offBeat && rng() < 0.16) openHat[s] = true;
    }

    // --- Bass: root-driven, with fifths and the flat-seventh as passing tones.
    const bassChoices = [chord.root, chord.root, chord.root, chord.root + 12, chord.root + 7, chord.root + chord.seventh - 12];
    for (let beat = 0; beat < 4; beat++) {
      const cell = BASS_CELLS[(rng() * BASS_CELLS.length) | 0]!;
      for (const off of cell) {
        const s = beat * 4 + off;
        bass[s] = bassChoices[(rng() * bassChoices.length) | 0]!;
      }
    }
    bass[0] = chord.root;

    // --- Arp: chord tones cycled with a rotation that changes every bar.
    const tones = [chord.root, chord.root + chord.third, chord.root + 7, chord.root + chord.seventh, chord.root + 12];
    const rot = (rng() * tones.length) | 0;
    const dirUp = rng() < 0.68;
    const gapEvery = 3 + ((rng() * 3) | 0);
    for (let s = 0; s < STEPS_PER_BAR; s++) {
      if (s % gapEvery === gapEvery - 1) continue;
      const k = dirUp ? s + rot : STEPS_PER_BAR - s + rot;
      const oct = ((k / tones.length) | 0) % 2 === 1 ? 12 : 0;
      arp[s] = tones[((k % tones.length) + tones.length) % tones.length]! + 24 + oct;
    }

    // --- Lead stabs: rare, syncopated, and only in the back half of a phrase.
    if (phraseStep >= 2) {
      const slots = [3, 6, 10, 11, 14];
      for (const s of slots) {
        if (rng() < 0.42) lead[s] = chord.root + (rng() < 0.5 ? 12 : chord.third + 12);
      }
    }

    // --- Perc: metallic 16ths that fill the gaps the kit leaves.
    for (let s = 0; s < STEPS_PER_BAR; s++) {
      if (kick[s]! > 0 || snare[s]! > 0.4) continue;
      if (rng() < (dense ? 0.16 : 0.07)) perc[s] = 0.4 + rng() * 0.5;
    }

    return { chord, kick, snare, hat, openHat, bass, arp, lead, perc };
  }

  private scheduleStep(step: number, at: number): void {
    const s = step % STEPS_PER_BAR;
    const bar = Math.floor(step / STEPS_PER_BAR);
    if (s === 0 || !this.plan) this.plan = this.makePlan(bar);
    const plan = this.plan;
    const L = this.layers;

    // Swing: push the odd 16ths late. Subtle — this is electro, not shuffle.
    const swing = s % 2 === 1 ? STEP_SEC * 0.055 : 0;
    const t = at + swing;

    if (L.drums.target > 0.01) {
      if (plan.kick[s]! > 0) {
        this.kick(t, plan.kick[s]!);
        this.sidechain(t);
      }
      if (plan.snare[s]! > 0) this.snare(t, plan.snare[s]!);
      if (plan.hat[s]! > 0.02) this.hat(t, plan.hat[s]!, plan.openHat[s]!);
    }

    if (L.bass.target > 0.01) {
      const n = plan.bass[s];
      if (n !== null && n !== undefined) {
        // Length runs to the next bass note so the line breathes instead of machine-gunning.
        let len = 1;
        while (len < 6 && plan.bass[(s + len) % STEPS_PER_BAR] === null) len++;
        this.bassNote(t, noteHz(n), 0.6 + 0.4 * (s === 0 ? 1 : 0.5), STEP_SEC * len * 0.85);
      }
    }

    if (L.arp.target > 0.01) {
      const n = plan.arp[s];
      if (n !== null && n !== undefined) this.pluck(t, noteHz(n), s % 4 === 0 ? 1 : 0.6);
    }

    if (L.perc.target > 0.01 && plan.perc[s]! > 0) this.percHit(t, plan.perc[s]!);

    if (L.lead.target > 0.01) {
      const n = plan.lead[s];
      if (n !== null && n !== undefined) this.stab(t, n, plan.chord);
    }

    // Pad moves on the bar line only.
    if (L.pad.target > 0.01 && s === 0) {
      this.pad(t, plan.chord, STEP_SEC * STEPS_PER_BAR);
    }
  }

  // -------------------------------------------------------------------------
  // Instruments
  // -------------------------------------------------------------------------

  /** Sidechain pump: the bus dips on every kick. Groove and headroom in one gesture. */
  private sidechain(t: number): void {
    const p = this.pump.gain;
    p.cancelScheduledValues(t);
    p.setValueAtTime(1, t);
    p.linearRampToValueAtTime(0.52, t + 0.012);
    p.linearRampToValueAtTime(1, t + 0.16);
  }

  private kick(t: number, vel: number): void {
    const ctx = this.ctx;
    const dest = this.layers.drums.gain;

    const o = osc(ctx, 'sine', 150);
    pitchEnv(o.frequency, t, 165, 44, 0.055);
    const g = gain(ctx, 0);
    percEnv(g.gain, t, 0.001, 0.26, 0.95 * vel);
    const sat = shaper(ctx, this.curveSat, '2x');
    o.connect(g);
    g.connect(sat);
    sat.connect(dest);
    play(o, t, t + 0.5);

    // Beater click so it cuts on small speakers.
    const n = this.noise.source('white', 1);
    const hp = biquad(ctx, 'highpass', 1600, 0.7);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t, 0.0004, 0.014, 0.18 * vel);
    n.connect(hp);
    hp.connect(ng);
    ng.connect(dest);
    startAtRandomPhase(n, t, this.rng);
    stopSafe(n, t + 0.06);
  }

  private snare(t: number, vel: number): void {
    const ctx = this.ctx;
    const dest = this.layers.drums.gain;
    const ghost = vel < 0.4;

    const n = this.noise.source('white', 1);
    const bp = biquad(ctx, 'bandpass', ghost ? 2600 : 1750, ghost ? 2.2 : 0.9);
    const hp = biquad(ctx, 'highpass', 420, 0.7);
    const ng = gain(ctx, 0);
    percEnv(ng.gain, t, 0.0008, ghost ? 0.035 : 0.12, 0.55 * vel);
    n.connect(bp);
    bp.connect(hp);
    hp.connect(ng);
    ng.connect(dest);
    startAtRandomPhase(n, t, this.rng);
    stopSafe(n, t + 0.3);

    // Two tuned shells give it pitch and stop it sounding like plain noise.
    [188, 268].forEach((hz, i) => {
      const o = osc(ctx, 'triangle', hz);
      pitchEnv(o.frequency, t, hz * 1.25, hz * 0.9, 0.03);
      const g = gain(ctx, 0);
      percEnv(g.gain, t, 0.001, ghost ? 0.03 : 0.07, (0.3 - i * 0.12) * vel);
      o.connect(g);
      g.connect(dest);
      play(o, t, t + 0.2);
    });

    if (!ghost && this.verbSend) {
      const send = gain(ctx, 0.22 * vel);
      const sn = this.noise.source('white', 1);
      const sbp = biquad(ctx, 'bandpass', 2200, 1.1);
      const sg = gain(ctx, 0);
      percEnv(sg.gain, t, 0.001, 0.1, 0.5);
      sn.connect(sbp);
      sbp.connect(sg);
      sg.connect(send);
      send.connect(this.verbSend);
      startAtRandomPhase(sn, t, this.rng);
      stopSafe(sn, t + 0.25);
    }
  }

  private hat(t: number, vel: number, open: boolean): void {
    const ctx = this.ctx;
    const dest = this.layers.drums.gain;
    const n = this.noise.source('white', 1.4 + this.rng() * 0.3);
    const hp = biquad(ctx, 'highpass', open ? 7000 : 8600, 0.8);
    const bp = biquad(ctx, 'bandpass', open ? 9000 : 11500, 1.1);
    const g = gain(ctx, 0);
    percEnv(g.gain, t, 0.0004, open ? 0.16 : 0.021, 0.2 * vel);
    n.connect(hp);
    hp.connect(bp);
    bp.connect(g);
    g.connect(dest);
    startAtRandomPhase(n, t, this.rng);
    stopSafe(n, t + (open ? 0.35 : 0.08));
  }

  private percHit(t: number, vel: number): void {
    const ctx = this.ctx;
    const dest = this.layers.perc.gain;
    const hz = 1400 + this.rng() * 2400;
    const o = osc(ctx, 'square', hz);
    pitchEnv(o.frequency, t, hz * 1.3, hz * 0.85, 0.02);
    const bp = biquad(ctx, 'bandpass', hz, 6);
    const g = gain(ctx, 0);
    percEnv(g.gain, t, 0.0005, 0.05 + this.rng() * 0.06, 0.16 * vel);
    o.connect(bp);
    bp.connect(g);
    g.connect(dest);
    play(o, t, t + 0.2);
  }

  private bassNote(t: number, hz: number, vel: number, dur: number): void {
    const ctx = this.ctx;
    const dest = this.layers.bass.gain;
    const f = clamp(hz, 41, 220);

    const o1 = osc(ctx, 'sawtooth', f, -5);
    const o2 = osc(ctx, 'square', f, +6);
    const sub = osc(ctx, 'sine', f * 0.5);

    const lp = biquad(ctx, 'lowpass', 400, 6.5);
    const cutHi = lerpN(700, 2600, vel) * lerpN(0.7, 1.3, this.intensity);
    sweep(lp.frequency, t, cutHi, 320, Math.min(dur * 0.8, 0.22));

    const drive = shaper(ctx, this.curveDrive, '2x');
    const g = gain(ctx, 0);
    // Hold for the note length, then a short release — the line has to breathe between notes.
    percEnv(g.gain, t, 0.003, 0.075, 0.5 * vel, Math.max(dur - 0.075, 0.01));

    const subG = gain(ctx, 0.5);
    o1.connect(lp);
    o2.connect(lp);
    lp.connect(drive);
    drive.connect(g);
    sub.connect(subG);
    subG.connect(g);
    g.connect(dest);

    play(o1, t, t + dur + 0.25);
    play(o2, t, t + dur + 0.25);
    play(sub, t, t + dur + 0.25);
  }

  private pluck(t: number, hz: number, vel: number): void {
    const ctx = this.ctx;
    const dest = this.layers.arp.gain;
    const o1 = osc(ctx, 'triangle', hz);
    const o2 = osc(ctx, 'sawtooth', hz, 7);
    const o2g = gain(ctx, 0.3);
    const lp = biquad(ctx, 'lowpass', 3000, 3.0);
    sweep(lp.frequency, t, 5200 * lerpN(0.6, 1.2, vel), 900, 0.14);
    const g = gain(ctx, 0);
    percEnv(g.gain, t, 0.001, 0.11 + 0.06 * vel, 0.2 * vel);

    o1.connect(lp);
    o2.connect(o2g);
    o2g.connect(lp);
    lp.connect(g);
    g.connect(dest);
    const send = gain(ctx, 0.3 * vel);
    g.connect(send);
    send.connect(this.delaySend);

    play(o1, t, t + 0.4);
    play(o2, t, t + 0.4);
  }

  private stab(t: number, semis: number, chord: { root: number; third: number; seventh: number }): void {
    const ctx = this.ctx;
    const dest = this.layers.lead.gain;
    const notes = [semis + 12, semis + 12 + chord.third, semis + 19];
    const lp = biquad(ctx, 'lowpass', 1200, 7.0);
    sweep(lp.frequency, t, 4800, 700, 0.16);
    const drive = shaper(ctx, this.curveDrive, '2x');
    const g = gain(ctx, 0);
    percEnv(g.gain, t, 0.002, 0.16, 0.28);
    lp.connect(drive);
    drive.connect(g);
    g.connect(dest);
    if (this.verbSend) {
      const send = gain(ctx, 0.3);
      g.connect(send);
      send.connect(this.verbSend);
    }
    for (const n of notes) {
      const o = osc(ctx, 'sawtooth', noteHz(n), (this.rng() - 0.5) * 14);
      o.connect(lp);
      play(o, t, t + 0.4);
    }
  }

  private pad(t: number, chord: { root: number; third: number; seventh: number }, dur: number): void {
    const ctx = this.ctx;
    const dest = this.layers.pad.gain;
    const notes = [chord.root + 12, chord.root + 12 + chord.third, chord.root + 19, chord.root + 12 + chord.seventh];
    const lp = biquad(ctx, 'lowpass', 1500, 0.9);
    sweep(lp.frequency, t, 900, 2100, dur * 0.6, false);
    const g = gain(ctx, 0);
    percEnv(g.gain, t, dur * 0.35, dur * 0.65, 0.16, 0);
    lp.connect(g);
    g.connect(dest);
    if (this.verbSend) {
      const send = gain(ctx, 0.7);
      g.connect(send);
      send.connect(this.verbSend);
    }
    for (const n of notes) {
      for (let d = -1; d <= 1; d += 2) {
        const o = osc(ctx, 'sawtooth', noteHz(n), d * (5 + this.rng() * 5));
        const og = gain(ctx, 0.28);
        o.connect(og);
        og.connect(lp);
        play(o, t, t + dur + 0.6);
      }
    }
  }
}
